import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ApplicationForm from "./components/ApplicationForm";
import { tiers, memberTier } from "./data";

const corePrinciples = [
  { icon: "ri-lock-line", title: "高門檻", desc: "嚴格審核資格，確保每一位成員均具備專業實力與行業影響力。" },
  { icon: "ri-vip-diamond-line", title: "高價值", desc: "享有品牌背書、資源調配、技術傳授等專屬權益，回報遠超投入。" },
  { icon: "ri-medal-line", title: "高榮譽", desc: "由國際學會認證的職級身份，代表行業地位與專業榮耀。" },
];

const steps = [
  { step: "01", title: "提交申請", desc: "填妥加盟申請表，留下聯絡資料與期望合作方式。" },
  { step: "02", title: "資格審核", desc: "按職級入會資格進行審核，確認你的專業背景與條件。" },
  { step: "03", title: "面談交流", desc: "安排面談，詳細介紹職級權益、學會使命與合作細節。" },
  { step: "04", title: "確認入會", desc: "雙方確認職級與權益，完成入會程序。" },
  { step: "05", title: "頒授職級", desc: "正式頒授職級身份，開通專屬權益並展開協作。" },
];

export default function Franchise() {
  return (
    <div className="min-h-screen bg-white text-brand-900">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-brand-50/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">加入我們</div>
          <h1 className="mt-6 font-serif text-3xl md:text-5xl text-brand-800">成為加盟商，共拓美業大健康版圖</h1>
          <p className="mt-4 text-brand-700/70 max-w-2xl text-sm md:text-base">
            ISHB 設有清晰的職級制度與專屬權益，誠邀產品廠家、治療專家、店舖與學院老闆及美業從業者加入，共享國際品牌資源，將專業技術傳承至世界。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#apply"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm tracking-wider whitespace-nowrap cursor-pointer transition"
            >
              立即申請加盟 <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="https://wa.me/85269438757"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md border border-brand-300 text-brand-700 hover:bg-brand-50 text-sm tracking-wider whitespace-nowrap cursor-pointer transition"
            >
              <i className="ri-whatsapp-line"></i> WhatsApp 查詢
            </a>
          </div>
        </div>
      </section>

      {/* 核心原則 */}
      <section className="py-14 md:py-16 bg-brand-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl md:text-3xl text-white">理事職級與權益 · 核心原則</h2>
            <p className="mt-3 text-white/70 text-sm">高門檻、高價值、高榮譽</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {corePrinciples.map((p) => (
              <div key={p.title} className="bg-white/5 border border-white/10 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-white/10 text-white">
                  <i className={`${p.icon} text-xl`}></i>
                </div>
                <h3 className="mt-4 font-serif text-lg text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 職級權益 */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl text-brand-800">職級權益</h2>
            <p className="mt-3 text-sm text-brand-700/60">由主席到理事，每個職級均設名額、資格與專屬年度權益</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiers.map((t) => (
              <article
                key={t.id}
                className={`rounded-lg border p-6 flex flex-col ${
                  t.highlight ? "bg-brand-800 border-brand-800 text-white" : "bg-white border-brand-100"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={`font-serif text-xl ${t.highlight ? "text-white" : "text-brand-800"}`}>{t.name}</h3>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                      t.highlight ? "bg-white/15 text-white" : "bg-brand-100 text-brand-700"
                    }`}
                  >
                    <i className="ri-group-line"></i> 名額 {t.seats}
                  </span>
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  <div>
                    <div className={`flex items-center gap-1.5 font-medium ${t.highlight ? "text-white/80" : "text-brand-900"}`}>
                      <i className="ri-user-received-line"></i> 入會資格
                    </div>
                    <p className={`mt-1 leading-relaxed ${t.highlight ? "text-white/70" : "text-brand-700/70"}`}>{t.qualification}</p>
                  </div>
                  <div>
                    <div className={`flex items-center gap-1.5 font-medium ${t.highlight ? "text-white/80" : "text-brand-900"}`}>
                      <i className="ri-award-line"></i> 年度權益 / 使命
                    </div>
                    <p className={`mt-1 leading-relaxed ${t.highlight ? "text-white/70" : "text-brand-700/70"}`}>{t.benefits}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* 會員職級：表格 */}
          <div className="mt-8 bg-brand-50/60 border border-brand-100 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-brand-100 flex items-center gap-2">
              <i className="ri-user-star-line text-brand-600"></i>
              <h3 className="font-serif text-lg text-brand-800">會員職級</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="bg-white text-left text-brand-900">
                    <th className="px-5 py-3 font-medium whitespace-nowrap">職級</th>
                    <th className="px-5 py-3 font-medium whitespace-nowrap">入會人數</th>
                    <th className="px-5 py-3 font-medium whitespace-nowrap">入會資格</th>
                    <th className="px-5 py-3 font-medium">年度權益 / 使命</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-brand-700/80">
                    <td className="px-5 py-4 font-serif text-lg text-brand-800 whitespace-nowrap">{memberTier.name}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{memberTier.seats}</td>
                    <td className="px-5 py-4 leading-relaxed">{memberTier.qualification}</td>
                    <td className="px-5 py-4 leading-relaxed">{memberTier.benefits}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* 加盟流程 */}
      <section className="py-16 md:py-20 bg-brand-50/60">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl text-brand-800">加盟流程</h2>
            <p className="mt-3 text-sm text-brand-700/60">由提交申請到頒授職級，五步完成入會</p>
          </div>
          <ol className="space-y-5">
            {steps.map((s) => (
              <li key={s.step} className="flex gap-4 bg-white border border-brand-100 rounded-lg p-5">
                <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-brand-600 text-white font-semibold text-sm">
                  {s.step}
                </div>
                <div>
                  <h4 className="font-semibold text-brand-900">{s.title}</h4>
                  <p className="mt-1 text-sm text-brand-700/70 leading-relaxed">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-brand-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-2xl md:text-4xl text-white">準備好與 ISHB 並肩同行？</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto text-sm md:text-base">
            立即提交加盟申請，讓我們的加盟顧問為你詳細介紹職級權益與合作方案，開啟你的美業事業新篇章。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#apply"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-white text-brand-800 hover:bg-brand-50 text-sm tracking-wider whitespace-nowrap cursor-pointer transition"
            >
              立即申請加盟
            </a>
            <Link
              to="/team"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md border border-white/70 text-white hover:bg-white/10 text-sm tracking-wider whitespace-nowrap cursor-pointer transition"
            >
              認識專業團隊
            </Link>
          </div>
        </div>
      </section>

      <ApplicationForm />

      <Footer />
    </div>
  );
}